import React, { useEffect, useState } from 'react';
import { CheckCircleFill, Clipboard, ClipboardCheck, ExclamationTriangleFill, ShieldCheck } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { shortHash } from '../prizeWheelShape';
import { verifyDraw, webCrypto } from '../prizeWheelVerify';

function CopyButton({ value, label }) {
  const { t } = useLocale();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => setDone(false), 1600);
    return () => clearTimeout(id);
  }, [done]);
  if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return null;
  return (
    <button
      type="button"
      className="wheel-proof__copy"
      onClick={() => navigator.clipboard.writeText(value).then(() => setDone(true), () => {})}
      aria-label={t('wheel.proof.copy', { what: label })}
      title={t('wheel.proof.copy', { what: label })}
    >
      {done ? <ClipboardCheck size={13} /> : <Clipboard size={13} />}
    </button>
  );
}

/**
 * The fairness proof: the seed's hash (published at scheduling), the seed
 * (after the draw) and — once both are there — a check in the browser.
 */
export default function WheelProof({ wheel, winners }) {
  const { t } = useLocale();
  const [check, setCheck] = useState(null); // null | 'busy' | { hashOk, winnersOk } | 'error'
  const seed = wheel.seed;
  const canVerify = wheel.status === 'finished' && !!seed && !!wheel.seedHash && !!webCrypto();
  useEffect(() => setCheck(null), [wheel.id, seed]);

  async function run() {
    setCheck('busy');
    try {
      const r = await verifyDraw({
        wheelId: wheel.id,
        seed,
        seedHash: wheel.seedHash,
        participantCount: wheel.participantCount,
        winnerCount: wheel.winnerCount,
        winners: winners || [],
        excludePreviousWinners: wheel.excludePreviousWinners !== false,
      });
      setCheck(r);
    } catch {
      setCheck('error');
    }
  }

  const ok = check && typeof check === 'object' && check.hashOk && check.winnersOk !== false;
  return (
    <div className="wheel-proof">
      <div className="wheel-proof__title">
        <ShieldCheck size={15} aria-hidden="true" /> {t('wheel.proof.title')}
      </div>
      <dl className="wheel-proof__rows">
        <div className="wheel-proof__row">
          <dt>{t('wheel.proof.seedHash')}</dt>
          <dd>
            {wheel.seedHash ? (
              <>
                <code title={wheel.seedHash}>{shortHash(wheel.seedHash, 16, 8)}</code>
                <CopyButton value={wheel.seedHash} label={t('wheel.proof.seedHash')} />
              </>
            ) : '—'}
          </dd>
        </div>
        <div className="wheel-proof__row">
          <dt>{t('wheel.proof.seed')}</dt>
          <dd>
            {seed ? (
              <>
                <code title={seed}>{shortHash(seed, 16, 8)}</code>
                <CopyButton value={seed} label={t('wheel.proof.seed')} />
              </>
            ) : (
              <span className="gift-muted">{t('wheel.proof.seedHidden')}</span>
            )}
          </dd>
        </div>
      </dl>
      <p className="wheel-proof__text">
        {wheel.status === 'finished' ? t('wheel.proof.textFinished') : t('wheel.proof.textScheduled')}
      </p>
      {canVerify && (
        <div className="wheel-proof__check">
          <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={run} disabled={check === 'busy'}>
            <ShieldCheck size={14} /> {check === 'busy' ? t('wheel.proof.checking') : t('wheel.proof.check')}
          </button>
          {check && check !== 'busy' && (
            <span className={`wheel-proof__result${ok ? ' is-ok' : ' is-bad'}`} role="status">
              {check === 'error' ? (
                t('wheel.proof.checkError')
              ) : ok ? (
                <><CheckCircleFill size={13} aria-hidden="true" /> {check.winnersOk === null ? t('wheel.proof.okHash') : t('wheel.proof.ok')}</>
              ) : (
                <><ExclamationTriangleFill size={13} aria-hidden="true" /> {!check.hashOk ? t('wheel.proof.badHash') : t('wheel.proof.badWinners')}</>
              )}
            </span>
          )}
        </div>
      )}
      <details className="wheel-proof__how">
        <summary>{t('wheel.proof.howTitle')}</summary>
        <ol>
          <li>{t('wheel.proof.how1')}</li>
          <li>{t('wheel.proof.how2')}</li>
          <li>
            {t('wheel.proof.how3')} <code>{t('wheel.proof.how3Code', { id: wheel.id })}</code>
          </li>
        </ol>
      </details>
    </div>
  );
}

